/**
 * SEO-optimized slug generation from product names
 * Converts product names to URL-friendly slugs following SEO best practices
 */
export function slugify(value?: string | null): string {
  if (!value) return "";
  
  return value
    .toLowerCase()
    .trim()
    // Replace accented characters with their base equivalents (é -> e, ñ -> n, etc.)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    // Replace spaces and special characters with hyphens
    .replace(/[^a-z0-9]+/g, "-")
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, "")
    // Limit length for SEO (max 100 chars, but keep it reasonable)
    .substring(0, 100)
    .replace(/-+$/g, ""); // Remove trailing hyphens after truncation
}

import { 
  parseProduct, 
  generateProductSlug as generateSlugFromParsed,
  generateSEOTitle,
  generateSEODescription
} from './product-parser';

/**
 * Generate a product slug from product name (always use name, not stored slug)
 * Falls back to product ID if name is not available
 * Uses ruleset-based parsing for cannabis and accessories
 */
export function generateProductSlug(product: {
  name?: string | null;
  id?: string;
  brandName?: string | null;
  classificationName?: string | null;
  sizeOrWeight?: string | null;
}): string {
  if (!product.name) {
    return product.id || "";
  }
  
  // Use new ruleset-based parser
  const parsed = parseProduct(
    product.name,
    product.classificationName || null,
    product.brandName || null,
    product.sizeOrWeight || null
  );
  
  const generatedSlug = generateSlugFromParsed(parsed);
  if (generatedSlug) {
    return generatedSlug;
  }
  
  // Fallback to product ID (last resort)
  return product.id || "";
}

/**
 * Generate a variant slug from packageId (SKU)
 * Used for variant parameter in URL
 */
export function generateVariantSlug(packageId: string): string {
  return slugify(packageId);
}

/**
 * Detect brand from product name if brandName is not provided
 * Looks for brand patterns like "Brand Name | Product" or "Brand Name - Product"
 */
export function detectBrandFromName(productName: string): string | null {
  if (!productName) return null;

  // Pattern 1: "Brand | Product" or "Brand | Product Name"
  const pipeMatch = productName.match(/^([^|]+)\s*\|\s*/);
  if (pipeMatch) {
    const brand = pipeMatch[1].trim();
    if (brand.length > 0 && brand.length < 50) { // Reasonable brand name length
      return brand;
    }
  }

  // Pattern 2: "Brand - Product" at the start
  const dashMatch = productName.match(/^([^-]+?)\s*[-–—]\s+/);
  if (dashMatch) {
    const brand = dashMatch[1].trim();
    // Check if it's not a size pattern (e.g., "10x0.35g")
    if (brand.length > 0 && brand.length < 50 && !/^\d+x?\d*\.?\d*\s*g$/i.test(brand)) {
      return brand;
    }
  }

  return null;
}

/**
 * Clean product name for display by removing brand and size information
 * Since brand is shown as a badge and size is shown separately
 * Also removes "|" characters
 */
export function cleanProductName(
  productName: string,
  brandName?: string | null
): string {
  if (!productName) return "";

  let cleaned = productName.trim();

  // Detect brand if not provided (do this BEFORE removing pipes, as pipes help detect brand)
  const detectedBrand = brandName || detectBrandFromName(cleaned);
  
  // Remove brand name from various positions
  if (detectedBrand) {
    const escapedBrand = detectedBrand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const originalCleaned = cleaned; // Keep original for reference
    
    // Pattern 1: Brand at the start: "Brand | Product" or "Brand - Product"
    const brandAtStart = new RegExp(`^${escapedBrand}\\s*[|\\-–—]?\\s*`, "i");
    cleaned = cleaned.replace(brandAtStart, "");
    
    // Pattern 2: Brand after pipe: "Product | Brand"
    const brandAfterPipe = new RegExp(`\\s*\\|\\s*${escapedBrand}\\s*[|\\-–—]?\\s*`, "i");
    cleaned = cleaned.replace(brandAfterPipe, " ");
    
    // Pattern 3: Brand after dash: "Product - Brand"
    const brandAfterDash = new RegExp(`\\s*[-–—]\\s*${escapedBrand}\\s*[|\\-–—]?\\s*`, "i");
    cleaned = cleaned.replace(brandAfterDash, " ");
    
    // Pattern 4: Brand anywhere else (be careful - only if standalone word)
    // Check if brand still exists after previous removals
    if (cleaned.toLowerCase().includes(detectedBrand.toLowerCase())) {
      const brandStandalone = new RegExp(`\\b${escapedBrand}\\b\\s*[|\\-–—]?\\s*`, "gi");
      cleaned = cleaned.replace(brandStandalone, (match, offset, string) => {
        // Don't remove if it's part of a larger word
        if (offset > 0 && /[a-zA-Z0-9]/.test(string[offset - 1])) return match;
        if (offset + match.length < string.length && /[a-zA-Z0-9]/.test(string[offset + match.length])) return match;
        return " ";
      });
    }
  }

  // Remove all "|" characters (after brand removal)
  cleaned = cleaned.replace(/\|/g, "");

  // Remove size patterns (e.g., "10x0.35g", "3x0.75g", "1g", "0.5g", "28g", etc.)
  // Match patterns like: "10x0.35g", "3x0.75g", "1g", "0.5g", "28g", "1.0g", etc.
  cleaned = cleaned.replace(/\s*[-–—]?\s*\d+x?\d*\.?\d*\s*g\s*/gi, "");
  
  // Remove standalone size patterns at the end (e.g., " - 10x0.35g")
  cleaned = cleaned.replace(/\s*[-–—]\s*\d+x?\d*\.?\d*\s*g\s*$/i, "");
  
  // Remove package patterns (e.g., "10 Pack", "3 Pack", etc.)
  cleaned = cleaned.replace(/\s*[-–—]?\s*\d+\s*pack\s*/gi, "");
  
  // Clean up multiple spaces and dashes
  cleaned = cleaned.replace(/\s*[-–—]+\s*/g, " ");
  cleaned = cleaned.replace(/\s+/g, " ");

  // Remove leading/trailing dashes and spaces
  cleaned = cleaned.replace(/^[-–—\s]+|[-–—\s]+$/g, "").trim();

  return cleaned || productName; // Fallback to original if cleaned is empty
}

/**
 * Extract brand name from product (from brandName field or detect from name)
 */
export function extractBrandName(
  productName: string,
  brandName?: string | null
): string | null {
  if (brandName) return brandName.trim();
  return detectBrandFromName(productName);
}

/**
 * Generate SEO title for a product according to ruleset
 */
export function generateProductSEOTitle(product: {
  name?: string | null;
  brandName?: string | null;
  classificationName?: string | null;
  sizeOrWeight?: string | null;
  seoTitle?: string | null;
}): string {
  const customTitle = (product.seoTitle || '').trim();
  if (!product.name) return customTitle || 'Dank';

  // Honor a curated seoTitle, but ignore an auto-populated one that just
  // embeds the raw, pipe-delimited product name (these often double the
  // brand, e.g. "Brand | Brand | Name | Class | Size"); regenerate instead.
  if (customTitle && !customTitle.includes(product.name.trim())) {
    return customTitle;
  }

  const parsed = parseProduct(
    product.name,
    product.classificationName || null,
    product.brandName || null,
    product.sizeOrWeight || null
  );

  return generateSEOTitle(parsed, null);
}

/**
 * Generate SEO description for a product according to ruleset
 */
export function generateProductSEODescription(product: {
  name?: string | null;
  brandName?: string | null;
  classificationName?: string | null;
  sizeOrWeight?: string | null;
  seoDescription?: string | null;
}): string {
  const customDesc = (product.seoDescription || '').trim();
  if (!product.name) return customDesc || 'Shop at Dank.';

  // Ignore an auto-populated seoDescription that just embeds the raw product
  // name; regenerate a clean description from the structured fields instead.
  if (customDesc && !customDesc.includes(product.name.trim())) {
    return customDesc;
  }

  const parsed = parseProduct(
    product.name,
    product.classificationName || null,
    product.brandName || null,
    product.sizeOrWeight || null
  );

  return generateSEODescription(parsed, null);
}

